/**
 * Map click interception for drawing tools.
 * When a drawing tool is active, captures map clicks to add vertices.
 */

import { useEffect } from 'react';
import type { MapInstance, ViewMode } from '@/map/map-setup';
import type { GeoPoint } from '@vtak/shared';
import { useDrawingStore } from '@/stores/drawing-store';
import { haversineDistance } from '@vtak/shared';
import {
  showDrawOverlay,
  updateDrawOverlay,
  clearDrawOverlay,
} from '@/map/drawing/draw-overlay';

/** Attach drawing interaction handlers to the map */
export function useDrawingInteraction(
  mapRef: React.RefObject<MapInstance | null>,
  modeRef: React.RefObject<ViewMode>,
): void {
  useEffect(() => {
    const checkAndAttach = setInterval(() => {
      const map = mapRef.current;
      if (!map) return;
      clearInterval(checkAndAttach);

      const mode = modeRef.current;
      if (mode === '2d') {
        attachDrawing2D(map as google.maps.Map);
      } else {
        attachDrawing3D(map as google.maps.maps3d.Map3DElement);
      }
    }, 100);

    return () => {
      clearInterval(checkAndAttach);
      clearDrawOverlay(modeRef.current);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        const store = useDrawingStore.getState();
        if (store.activeTool) {
          store.cancelDrawing();
          clearDrawOverlay(modeRef.current);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}

/** Attach 2D map click handler for drawing */
function attachDrawing2D(map: google.maps.Map): void {
  map.addListener('click', (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const store = useDrawingStore.getState();
    if (!store.activeTool || store.drawingState !== 'drawing') return;

    const point: GeoPoint = { lat: e.latLng.lat(), lon: e.latLng.lng() };
    handleDrawClick(point, map, '2d');
  });

  map.addListener('dblclick', () => {
    const store = useDrawingStore.getState();
    if (!store.activeTool || store.drawingState !== 'drawing') return;
    handleDoubleClick(store.activeTool);
  });

  map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const store = useDrawingStore.getState();
    if (!store.activeTool || store.drawingState !== 'drawing') return;
    handleMouseMove(store, e.latLng.lat(), e.latLng.lng(), map, '2d');
  });
}

/** Attach 3D map click handler for drawing */
function attachDrawing3D(map3d: google.maps.maps3d.Map3DElement): void {
  map3d.addEventListener('gmp-click', (e: Event) => {
    const clickEvent = e as google.maps.maps3d.LocationClickEvent;
    const position = clickEvent.position;
    if (!position) return;

    const store = useDrawingStore.getState();
    if (!store.activeTool || store.drawingState !== 'drawing') return;

    const point: GeoPoint = { lat: position.lat, lon: position.lng };
    handleDrawClick(point, map3d, '3d');
  });
}

/** Process a draw click for any tool */
function handleDrawClick(point: GeoPoint, map: MapInstance, mode: ViewMode): void {
  const store = useDrawingStore.getState();
  const tool = store.activeTool;
  if (!tool) return;

  store.addVertex(point);
  const vertices = [...store.vertices, point];

  if (shouldAutoFinish(tool, vertices)) {
    showDrawOverlay(map, mode, vertices, tool);
    store.finishDrawing();
    return;
  }

  if (isRadiusTool(tool) && vertices.length === 1) {
    showDrawOverlay(map, mode, vertices, tool);
  } else {
    updateDrawOverlay(map, mode, vertices, tool);
  }
}

/** Handle double-click to finish polygon/freeform drawing */
function handleDoubleClick(tool: string): void {
  if (tool === 'polygon' || tool === 'freeform') {
    const store = useDrawingStore.getState();
    if (store.vertices.length >= 3) {
      store.finishDrawing();
    }
  }
}

/** Handle mouse move for radius preview (circle/geofence) */
function handleMouseMove(
  store: ReturnType<typeof useDrawingStore.getState>,
  lat: number,
  lng: number,
  map: MapInstance,
  mode: ViewMode,
): void {
  const tool = store.activeTool;
  if (!tool) return;

  if (isRadiusTool(tool) && store.vertices.length === 1) {
    const center = store.vertices[0]!;
    const cursor: GeoPoint = { lat, lon: lng };
    const radius = haversineDistance(center, cursor);
    store.setRadius(radius);
    showDrawOverlay(map, mode, [center], tool);
  }
}

/** Check if a tool should auto-finish after a certain number of vertices */
function shouldAutoFinish(tool: string, vertices: GeoPoint[]): boolean {
  if (tool === 'measurement' && vertices.length >= 2) return true;
  if (tool === 'rectangle' && vertices.length >= 2) return true;
  if (isRadiusTool(tool) && vertices.length >= 2) return true;
  return false;
}

/** Check if a tool uses the radius interaction model */
function isRadiusTool(tool: string): boolean {
  return tool === 'circle' || tool === 'geofence';
}
